import { useCallback } from 'react';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import {
  updateElement,
  batchUpdateElements,
  deleteElement,
  createElement,
  createElementRelation,
  batchCreateElementRelations,
  updateElementRelation,
  deleteElementRelation,
  batchDeleteElementRelations,
  getElementById,
} from '../../api/element';
import { ElementInfo, CreateElementParams, ElementSceneInfo } from '../../types/element.types';

/** 服务端返回「关系已存在」类错误时，乐观缓存已与服务器一致，不应回滚也不必抛错 */
function isDuplicateElementRelationError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 409) return true;
  const msg =
    (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
    (error as { message?: string })?.message ??
    '';
  const s = typeof msg === 'string' ? msg : String(msg ?? '');
  return (
    s.includes('关系已存在') ||
    s.includes('已存在') ||
    /already\s+exists/i.test(s)
  );
}

/**
 * Element 更新 Hook 配置选项
 */
export interface UseElementUpdatesOptions {
  onSuccess?: (action: 'create' | 'update' | 'delete', data: any) => void;
  onError?: (error: Error, action: string, data: any) => void;
}

/**
 * Element 更新 Hook
 * 提供统一的 Element 和 ElementRelation 数据更新方法，包括：
 * - 单条/批量更新 Element
 * - 单条/批量删除 Element
 * - 单条创建 Element
 * - 创建单个/批量 ElementRelation
 * - 更新 ElementRelation
 * - 删除单个/批量 ElementRelation
 * - 统一的字段更新方法（自动路由到正确的 API）
 * 
 * 更新流程：
 * 1. 乐观更新缓存
 * 2. 通知本标签页订阅者（UI 立即更新）
 * 3. 调用 API
 * 4. 成功：通过 BroadcastChannel 通知其他标签页
 * 5. 失败：回滚缓存，通知订阅者
 */
export function useElementUpdates(options?: UseElementUpdatesOptions) {
  const {
    getElementCache,
    setElementCache,
    getElementRelationCache,
    setElementRelationCache,
    getBreakdownCache,
    setBreakdownCache,
  } = useDataCache();
  
  // setBreakdownCache 和 setElementRelationCache 在 useCallback 内部使用
  // ESLint 可能误报为未使用，使用 eslint-disable 注释避免警告
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unused1 = setBreakdownCache;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unused2 = setElementRelationCache;
  void _unused1;
  void _unused2;

  /**
   * 更新单个 Element
   */
  const updateElementData = useCallback(
    async (breakdownId: string, elementId: string, updates: Partial<ElementInfo>) => {
      let oldCache: any = null;
      let oldElements: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownElements: any[] = [];

      try {
        // 乐观更新 Element 缓存
        const cache = getElementCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldElements = [...cache.elements];

          const updatedElements = cache.elements.map((item) =>
            item.elementID === elementId ? { ...item, ...updates } : item
          );

          setElementCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 同时更新 Breakdown 缓存中的 elements（如果存在）
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elements) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownElements = [...breakdownCache.elements];

          const updatedElements = breakdownCache.elements.map((item) =>
            item.elementID === elementId ? { ...item, ...updates } : item
          );

          setBreakdownCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 调用 API
        await updateElement(elementId, updates);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { elementId, updates });
        }
      } catch (error) {
        console.error('[useElementUpdates] 更新 Element 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setElementCache(breakdownId, {
            elements: oldElements,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elements: oldBreakdownElements,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { elementId, updates });
        }

        throw error;
      }
    },
    [getElementCache, setElementCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 批量更新 Elements
   */
  const updateElementsData = useCallback(
    async (
      breakdownId: string,
      updates: Array<{ elementId: string; data: Partial<ElementInfo> }>
    ) => {
      let oldCache: any = null;
      let oldElements: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownElements: any[] = [];

      try {
        // 创建更新映射
        const updateMap = new Map(updates.map((u) => [u.elementId, u.data]));

        // 乐观更新 Element 缓存
        const cache = getElementCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldElements = [...cache.elements];

          const updatedElements = cache.elements.map((item) => {
            const update = updateMap.get(item.elementID);
            return update ? { ...item, ...update } : item;
          });

          setElementCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elements) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownElements = [...breakdownCache.elements];

          const updatedElements = breakdownCache.elements.map((item) => {
            const update = updateMap.get(item.elementID);
            return update ? { ...item, ...update } : item;
          });

          setBreakdownCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 批量调用 API（单次请求，提升性能）
        await batchUpdateElements(
          updates.map((u) => ({ elementID: u.elementId, data: u.data }))
        );

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { updates });
        }
      } catch (error) {
        console.error('[useElementUpdates] 批量更新 Elements 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setElementCache(breakdownId, {
            elements: oldElements,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elements: oldBreakdownElements,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { updates });
        }

        throw error;
      }
    },
    [getElementCache, setElementCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 删除单个 Element
   */
  const deleteElementData = useCallback(
    async (breakdownId: string, elementId: string) => {
      let oldCache: any = null;
      let oldElements: any[] = [];
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownElements: any[] = [];
      let oldBreakdownRelations: any[] = [];

      try {
        // 乐观删除 Element 缓存
        const cache = getElementCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldElements = [...cache.elements];

          const updatedElements = cache.elements.filter(
            (item) => item.elementID !== elementId
          );

          setElementCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 乐观删除 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          const updatedRelations = relationCache.elementRelations.filter(
            (rel) => rel.elementID !== elementId
          );

          setElementRelationCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache) {
          oldBreakdownCache = { ...breakdownCache };
          if (breakdownCache.elements) {
            oldBreakdownElements = [...breakdownCache.elements];
          }
          if (breakdownCache.elementRelations) {
            oldBreakdownRelations = [...breakdownCache.elementRelations];
          }

          const updates: any = {};
          if (breakdownCache.elements) {
            updates.elements = breakdownCache.elements.filter(
              (item) => item.elementID !== elementId
            );
          }
          if (breakdownCache.elementRelations) {
            updates.elementRelations = breakdownCache.elementRelations.filter(
              (rel) => rel.elementID !== elementId
            );
          }

          if (Object.keys(updates).length > 0) {
            setBreakdownCache(breakdownId, updates);
          }
        }

        // 调用 API
        await deleteElement(elementId);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { elementId });
        }
      } catch (error) {
        console.error('[useElementUpdates] 删除 Element 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setElementCache(breakdownId, {
            elements: oldElements,
          });
        }
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          const updates: any = {};
          if (oldBreakdownElements.length > 0) {
            updates.elements = oldBreakdownElements;
          }
          if (oldBreakdownRelations.length > 0) {
            updates.elementRelations = oldBreakdownRelations;
          }
          if (Object.keys(updates).length > 0) {
            setBreakdownCache(breakdownId, updates);
          }
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { elementId });
        }

        throw error;
      }
    },
    [getElementCache, setElementCache, getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 批量删除 Elements
   */
  const deleteElementsData = useCallback(
    async (breakdownId: string, elementIds: string[]) => {
      let oldCache: any = null;
      let oldElements: any[] = [];
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownElements: any[] = [];
      let oldBreakdownRelations: any[] = [];

      try {
        const elementIdSet = new Set(elementIds);

        // 乐观删除 Element 缓存
        const cache = getElementCache(breakdownId);
        if (cache) {
          oldCache = { ...cache };
          oldElements = [...cache.elements];

          const updatedElements = cache.elements.filter(
            (item) => !elementIdSet.has(item.elementID)
          );

          setElementCache(breakdownId, {
            elements: updatedElements,
          });
        }

        // 乐观删除 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          const updatedRelations = relationCache.elementRelations.filter(
            (rel) => !elementIdSet.has(rel.elementID)
          );

          setElementRelationCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache) {
          oldBreakdownCache = { ...breakdownCache };
          if (breakdownCache.elements) {
            oldBreakdownElements = [...breakdownCache.elements];
          }
          if (breakdownCache.elementRelations) {
            oldBreakdownRelations = [...breakdownCache.elementRelations];
          }

          const updates: any = {};
          if (breakdownCache.elements) {
            updates.elements = breakdownCache.elements.filter(
              (item) => !elementIdSet.has(item.elementID)
            );
          }
          if (breakdownCache.elementRelations) {
            updates.elementRelations = breakdownCache.elementRelations.filter(
              (rel) => !elementIdSet.has(rel.elementID)
            );
          }

          if (Object.keys(updates).length > 0) {
            setBreakdownCache(breakdownId, updates);
          }
        }

        // 批量调用 API
        await Promise.all(elementIds.map((id) => deleteElement(id)));

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { elementIds });
        }
      } catch (error) {
        console.error('[useElementUpdates] 批量删除 Elements 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setElementCache(breakdownId, {
            elements: oldElements,
          });
        }
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          const updates: any = {};
          if (oldBreakdownElements.length > 0) {
            updates.elements = oldBreakdownElements;
          }
          if (oldBreakdownRelations.length > 0) {
            updates.elementRelations = oldBreakdownRelations;
          }
          if (Object.keys(updates).length > 0) {
            setBreakdownCache(breakdownId, updates);
          }
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { elementIds });
        }

        throw error;
      }
    },
    [getElementCache, setElementCache, getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 创建单个 Element
   * @returns 新创建的 elementId
   */
  const createElementData = useCallback(
    async (breakdownId: string, data: CreateElementParams): Promise<string> => {
      try {
        // 1. 先调用 API 创建，获取真实 ID
        const response = await createElement(data);
        const newElementId = response.data?.elementID;

        if (!newElementId) {
          throw new Error('创建 Element 失败：未返回 elementID');
        }

        // 提取关系参数（后端会自动创建关系）
        const { sceneID, elementQtyInScene, ...elementData } = data;
        
        // 确定最大数量：如果提供了 elementQtyInScene，使用该值；否则默认为1
        const maxQty = elementQtyInScene !== undefined && elementQtyInScene !== null 
          ? elementQtyInScene 
          : 1;
        const relationQty = elementQtyInScene !== undefined && elementQtyInScene !== null
          ? elementQtyInScene
          : 1;

        // 2. 用真实 ID 更新 Element 缓存
        const cache = getElementCache(breakdownId);
        if (cache) {
          const newElement = {
            ...elementData,
            elementID: newElementId,
            elementMaxQtyInBreakdown: maxQty, // 设置最大数量
          } as ElementInfo;

          // 检查是否已存在（避免重复添加）
          const existingIndex = cache.elements.findIndex(
            item => item.elementID === newElementId
          );

          if (existingIndex === -1) {
            setElementCache(breakdownId, {
              elements: [...cache.elements, newElement],
            });
          }
        }

        // 3. 同时更新 Breakdown 缓存中的 elements
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elements) {
          const newElement = {
            ...elementData,
            elementID: newElementId,
            elementMaxQtyInBreakdown: maxQty, // 设置最大数量
          } as ElementInfo;

          const existingIndex = breakdownCache.elements.findIndex(
            item => item.elementID === newElementId
          );

          if (existingIndex === -1) {
            setBreakdownCache(breakdownId, {
              elements: [...breakdownCache.elements, newElement],
            });
          }
        }

        // 4. 如果提供了 sceneID，更新关系缓存（后端会自动创建关系）
        // 注意：只要提供了 sceneID，就应该更新关系缓存，即使 elementQtyInScene 为 undefined
        if (sceneID) {
          const relationCache = getElementRelationCache(breakdownId);
          const newRelation: Partial<ElementSceneInfo> = {
            elementID: newElementId,
            sceneID: sceneID,
            elementQtyInScene: relationQty, // 默认数量为1
          };

          if (relationCache) {
            // 检查关系是否已存在（避免重复添加）
            const existingIndex = relationCache.elementRelations.findIndex(
              (rel) => rel.elementID === newElementId && rel.sceneID === sceneID
            );

            if (existingIndex === -1) {
              console.log('[useElementUpdates] 更新关系缓存:', {
                breakdownId,
                newElementId,
                sceneID,
                relationQty,
                relationsCountBefore: relationCache.elementRelations.length,
                relationsCountAfter: relationCache.elementRelations.length + 1,
              });
              setElementRelationCache(breakdownId, {
                elementRelations: [...relationCache.elementRelations, newRelation as ElementSceneInfo],
              });
            } else {
              console.warn('[useElementUpdates] 关系已存在，跳过添加:', {
                newElementId,
                sceneID,
                existingIndex,
              });
            }
          } else {
            // 如果关系缓存不存在，创建一个新的
            console.log('[useElementUpdates] 创建新的关系缓存:', {
              breakdownId,
              newElementId,
              sceneID,
              relationQty,
            });
            setElementRelationCache(breakdownId, {
              elementRelations: [newRelation as ElementSceneInfo],
            });
          }

          // 5. 同时更新 Breakdown 缓存中的关系
          if (breakdownCache) {
            // 确保 elementRelations 数组存在
            const currentRelations = breakdownCache.elementRelations || [];
            
            const newRelation: Partial<ElementSceneInfo> = {
              elementID: newElementId,
              sceneID: sceneID,
              elementQtyInScene: relationQty, // 默认数量为1
            };

            const existingIndex = currentRelations.findIndex(
              (rel) => rel.elementID === newElementId && rel.sceneID === sceneID
            );

            if (existingIndex === -1) {
              setBreakdownCache(breakdownId, {
                elementRelations: [...currentRelations, newRelation as ElementSceneInfo],
              });
            }
          }
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('create', { elementId: newElementId, data });
        }

        return newElementId;
      } catch (error) {
        console.error('[useElementUpdates] 创建 Element 失败:', error);

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'create', { data });
        }

        throw error;
      }
    },
    [getElementCache, setElementCache, getBreakdownCache, setBreakdownCache, getElementRelationCache, setElementRelationCache, options]
  );

  /**
   * 创建单个 ElementRelation
   * 将已存在的 Element 关联到 Scene
   */
  const createElementRelationData = useCallback(
    async (
      breakdownId: string,
      elementId: string,
      sceneId: string,
      data?: { elementQtyInScene?: number }
    ) => {
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownRelations: any[] = [];

      try {
        // 构建新关系对象
        const newRelation: Partial<ElementSceneInfo> = {
          elementID: elementId,
          sceneID: sceneId,
          elementQtyInScene: data?.elementQtyInScene || 1,
        };

        // 乐观更新 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          // 检查关系是否已存在
          const existingIndex = relationCache.elementRelations.findIndex(
            (rel) => rel.elementID === elementId && rel.sceneID === sceneId
          );

          if (existingIndex === -1) {
            // 不存在，添加新关系
            setElementRelationCache(breakdownId, {
              elementRelations: [...relationCache.elementRelations, newRelation as ElementSceneInfo],
            });
          } else {
            // 已存在，更新关系
            const updatedRelations = [...relationCache.elementRelations];
            updatedRelations[existingIndex] = {
              ...updatedRelations[existingIndex],
              ...newRelation,
            };
            setElementRelationCache(breakdownId, {
              elementRelations: updatedRelations,
            });
          }
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elementRelations) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownRelations = [...breakdownCache.elementRelations];

          const existingIndex = breakdownCache.elementRelations.findIndex(
            (rel) => rel.elementID === elementId && rel.sceneID === sceneId
          );

          if (existingIndex === -1) {
            setBreakdownCache(breakdownId, {
              elementRelations: [...breakdownCache.elementRelations, newRelation as ElementSceneInfo],
            });
          } else {
            const updatedRelations = [...breakdownCache.elementRelations];
            updatedRelations[existingIndex] = {
              ...updatedRelations[existingIndex],
              ...newRelation,
            };
            setBreakdownCache(breakdownId, {
              elementRelations: updatedRelations,
            });
          }
        }

        // 调用 API
        const requestData = {
          elementID: elementId,
          sceneID: sceneId,
          elementQtyInScene: data?.elementQtyInScene,
        };
        console.log('[DEBUG] useElementUpdates.createElementRelationData 调用参数:', {
          breakdownId,
          elementId,
          sceneId,
          data,
          requestData: JSON.stringify(requestData, null, 2),
        });
        await createElementRelation(requestData);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('create', { elementId, sceneId, data });
        }

        console.log('[useElementUpdates] 创建关系成功:', { elementId, sceneId });
      } catch (error) {
        if (isDuplicateElementRelationError(error)) {
          // 关系已在服务端存在：保留乐观更新后的缓存，等同成功
          console.warn('[useElementUpdates] 创建关系跳过（已存在）:', { elementId, sceneId });
          if (options?.onSuccess) {
            options.onSuccess('create', { elementId, sceneId, data });
          }
          return;
        }

        console.error('[useElementUpdates] 创建关系失败:', error);

        // 回滚缓存
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elementRelations: oldBreakdownRelations,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'create', { elementId, sceneId, data });
        }

        throw error;
      }
    },
    [getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 批量创建 ElementRelations
   * 将多个已存在的 Element 关联到 Scene
   */
  const batchCreateElementRelationsData = useCallback(
    async (
      breakdownId: string,
      relations: Array<{
        elementID: string;
        sceneID: string;
        elementQtyInScene?: number;
      }>
    ) => {
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownRelations: any[] = [];

      try {
        // 构建新关系对象数组
        const newRelations: Partial<ElementSceneInfo>[] = relations.map((rel) => ({
          elementID: rel.elementID,
          sceneID: rel.sceneID,
          elementQtyInScene: rel.elementQtyInScene || 1,
        }));

        // 乐观更新 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          // 合并新关系和现有关系
          const mergedRelations = [...relationCache.elementRelations];
          newRelations.forEach((newRel) => {
            const existingIndex = mergedRelations.findIndex(
              (rel) => rel.elementID === newRel.elementID && rel.sceneID === newRel.sceneID
            );

            if (existingIndex === -1) {
              // 不存在，添加
              mergedRelations.push(newRel as ElementSceneInfo);
            } else {
              // 已存在，更新
              mergedRelations[existingIndex] = {
                ...mergedRelations[existingIndex],
                ...newRel,
              };
            }
          });

          setElementRelationCache(breakdownId, {
            elementRelations: mergedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elementRelations) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownRelations = [...breakdownCache.elementRelations];

          const mergedRelations = [...breakdownCache.elementRelations];
          newRelations.forEach((newRel) => {
            const existingIndex = mergedRelations.findIndex(
              (rel) => rel.elementID === newRel.elementID && rel.sceneID === newRel.sceneID
            );

            if (existingIndex === -1) {
              mergedRelations.push(newRel as ElementSceneInfo);
            } else {
              mergedRelations[existingIndex] = {
                ...mergedRelations[existingIndex],
                ...newRel,
              };
            }
          });

          setBreakdownCache(breakdownId, {
            elementRelations: mergedRelations,
          });
        }

        // 调用 API
        await batchCreateElementRelations(relations);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('create', { relations });
        }

        console.log('[useElementUpdates] 批量创建关系成功:', { count: relations.length });
      } catch (error) {
        console.error('[useElementUpdates] 批量创建关系失败:', error);

        // 回滚缓存
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elementRelations: oldBreakdownRelations,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'create', { relations });
        }

        throw error;
      }
    },
    [getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 更新 ElementRelation
   * 更新关系的数量和类型
   */
  const updateElementRelationData = useCallback(
    async (
      breakdownId: string,
      elementId: string,
      sceneId: string,
      updates: { elementQtyInScene?: number }
    ) => {
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownRelations: any[] = [];

      try {
        // 乐观更新 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          const updatedRelations = relationCache.elementRelations.map((rel) =>
            rel.elementID === elementId && rel.sceneID === sceneId
              ? { ...rel, ...updates }
              : rel
          );

          setElementRelationCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elementRelations) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownRelations = [...breakdownCache.elementRelations];

          const updatedRelations = breakdownCache.elementRelations.map((rel) =>
            rel.elementID === elementId && rel.sceneID === sceneId
              ? { ...rel, ...updates }
              : rel
          );

          setBreakdownCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 调用 API
        await updateElementRelation(elementId, sceneId, {
          elementQtyInScene: updates.elementQtyInScene,
        });

        // 更新关系成功后，需要同步更新 Element 缓存中的 elementMaxQtyInBreakdown
        // 因为后端会更新最大数量，但前端缓存中的 Element 数据还没有更新
        try {
          const updatedElementResponse = await getElementById(elementId);
          const updatedElement = updatedElementResponse.data || updatedElementResponse;
          
          if (updatedElement && updatedElement.elementMaxQtyInBreakdown !== undefined) {
            // 更新 Element 缓存
            const elementCache = getElementCache(breakdownId);
            if (elementCache && elementCache.elements) {
              const updatedElements = elementCache.elements.map((item) =>
                item.elementID === elementId
                  ? { ...item, elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown }
                  : item
              );
              
              setElementCache(breakdownId, {
                elements: updatedElements,
              });
            }
            
            // 同时更新 Breakdown 缓存中的 elements（如果存在）
            const breakdownCache = getBreakdownCache(breakdownId);
            if (breakdownCache && breakdownCache.elements) {
              const updatedElements = breakdownCache.elements.map((item) =>
                item.elementID === elementId
                  ? { ...item, elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown }
                  : item
              );
              
              setBreakdownCache(breakdownId, {
                elements: updatedElements,
              });
            }
            
            console.log('[useElementUpdates] 已同步更新 Element 缓存中的最大数量:', {
              elementId,
              elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown,
            });
          }
        } catch (error) {
          // 如果获取更新后的 Element 失败，记录错误但不影响主流程
          console.warn('[useElementUpdates] 获取更新后的 Element 失败，Element 缓存可能未更新:', error);
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('update', { elementId, sceneId, updates });
        }

        console.log('[useElementUpdates] 更新关系成功:', { elementId, sceneId });
      } catch (error) {
        console.error('[useElementUpdates] 更新关系失败:', error);

        // 回滚缓存
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elementRelations: oldBreakdownRelations,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'update', { elementId, sceneId, updates });
        }

        throw error;
      }
    },
    [getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, getElementCache, setElementCache, options]
  );

  /**
   * 删除单个 ElementRelation
   * 移除 Element 与 Scene 的关联关系
   */
  const deleteElementRelationData = useCallback(
    async (breakdownId: string, elementId: string, sceneId: string) => {
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownRelations: any[] = [];

      try {
        // 乐观删除 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          const updatedRelations = relationCache.elementRelations.filter(
            (rel) => !(rel.elementID === elementId && rel.sceneID === sceneId)
          );

          setElementRelationCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elementRelations) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownRelations = [...breakdownCache.elementRelations];

          const updatedRelations = breakdownCache.elementRelations.filter(
            (rel) => !(rel.elementID === elementId && rel.sceneID === sceneId)
          );

          setBreakdownCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 调用 API
        await deleteElementRelation({
          elementID: elementId,
          sceneID: sceneId,
        });

        // 删除关系成功后，需要同步更新 Element 缓存中的 elementMaxQtyInBreakdown
        // 因为后端可能会重新统计最大数量（如果删除的数量等于当前最大值）
        try {
          const updatedElementResponse = await getElementById(elementId);
          const updatedElement = updatedElementResponse.data || updatedElementResponse;
          
          if (updatedElement && updatedElement.elementMaxQtyInBreakdown !== undefined) {
            // 更新 Element 缓存
            const elementCache = getElementCache(breakdownId);
            if (elementCache && elementCache.elements) {
              const updatedElements = elementCache.elements.map((item) =>
                item.elementID === elementId
                  ? { ...item, elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown }
                  : item
              );
              
              setElementCache(breakdownId, {
                elements: updatedElements,
              });
            }
            
            // 同时更新 Breakdown 缓存中的 elements（如果存在）
            const breakdownCache = getBreakdownCache(breakdownId);
            if (breakdownCache && breakdownCache.elements) {
              const updatedElements = breakdownCache.elements.map((item) =>
                item.elementID === elementId
                  ? { ...item, elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown }
                  : item
              );
              
              setBreakdownCache(breakdownId, {
                elements: updatedElements,
              });
            }
            
            console.log('[useElementUpdates] 已同步更新 Element 缓存中的最大数量（删除关系后）:', {
              elementId,
              elementMaxQtyInBreakdown: updatedElement.elementMaxQtyInBreakdown,
            });
          }
        } catch (error) {
          // 如果获取更新后的 Element 失败，记录错误但不影响主流程
          console.warn('[useElementUpdates] 获取更新后的 Element 失败（删除关系后），Element 缓存可能未更新:', error);
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { elementId, sceneId });
        }

        console.log('[useElementUpdates] 删除关系成功:', { elementId, sceneId });
      } catch (error) {
        console.error('[useElementUpdates] 删除关系失败:', error);

        // 回滚缓存
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elementRelations: oldBreakdownRelations,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { elementId, sceneId });
        }

        throw error;
      }
    },
    [getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, getElementCache, setElementCache, options]
  );

  /**
   * 批量删除 ElementRelations
   * 批量移除 Element 与 Scene 的关联关系
   */
  const batchDeleteElementRelationsData = useCallback(
    async (
      breakdownId: string,
      relations: Array<{
        elementID: string;
        sceneID: string;
      }>
    ) => {
      let oldRelationCache: any = null;
      let oldRelations: any[] = [];
      let oldBreakdownCache: any = null;
      let oldBreakdownRelations: any[] = [];

      try {
        // 创建要删除的关系的 Set（用于快速查找）
        const relationsToDelete = new Set(
          relations.map((rel) => `${rel.elementID}-${rel.sceneID}`)
        );

        // 乐观删除 ElementRelation 缓存
        const relationCache = getElementRelationCache(breakdownId);
        if (relationCache) {
          oldRelationCache = { ...relationCache };
          oldRelations = [...relationCache.elementRelations];

          const updatedRelations = relationCache.elementRelations.filter(
            (rel) => !relationsToDelete.has(`${rel.elementID}-${rel.sceneID}`)
          );

          setElementRelationCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 同时更新 Breakdown 缓存
        const breakdownCache = getBreakdownCache(breakdownId);
        if (breakdownCache && breakdownCache.elementRelations) {
          oldBreakdownCache = { ...breakdownCache };
          oldBreakdownRelations = [...breakdownCache.elementRelations];

          const updatedRelations = breakdownCache.elementRelations.filter(
            (rel) => !relationsToDelete.has(`${rel.elementID}-${rel.sceneID}`)
          );

          setBreakdownCache(breakdownId, {
            elementRelations: updatedRelations,
          });
        }

        // 调用 API
        await batchDeleteElementRelations(relations);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { relations });
        }

        console.log('[useElementUpdates] 批量删除关系成功:', { count: relations.length });
      } catch (error) {
        console.error('[useElementUpdates] 批量删除关系失败:', error);

        // 回滚缓存
        if (oldRelationCache) {
          setElementRelationCache(breakdownId, {
            elementRelations: oldRelations,
          });
        }
        if (oldBreakdownCache) {
          setBreakdownCache(breakdownId, {
            elementRelations: oldBreakdownRelations,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { relations });
        }

        throw error;
      }
    },
    [getElementRelationCache, setElementRelationCache, getBreakdownCache, setBreakdownCache, options]
  );

  /**
   * 统一的字段更新方法
   * 根据字段名自动路由到正确的 API
   * - elementQtyInScene → 使用 updateElementRelationData
   * - 其他字段 → 使用 updateElementData
   */
  const updateElementField = useCallback(
    async (
      breakdownId: string,
      elementId: string,
      sceneId: string,
      field: string,
      value: any
    ) => {
      if (field === 'elementQtyInScene') {
        return await updateElementRelationData(breakdownId, elementId, sceneId, {
          elementQtyInScene: value,
        });
      } else {
        return await updateElementData(breakdownId, elementId, {
          [field]: value,
        });
      }
    },
    [updateElementData, updateElementRelationData]
  );

  return {
    // Element 操作方法
    updateElementData,
    updateElementsData,
    deleteElementData,
    deleteElementsData,
    createElementData,
    // ElementRelation 操作方法
    createElementRelationData,
    batchCreateElementRelationsData,
    updateElementRelationData,
    deleteElementRelationData,
    batchDeleteElementRelationsData,
    // 统一更新方法
    updateElementField,
  };
}
